import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Button from "../components/Common/Button";
import Checkbox from "../components/Common/Checkbox";
import ConfirmModal from "../components/Common/ConfirmModal";
import LoadingSpinner from "../components/Common/LoadingSpinner";
import NotificationModal from "../components/Common/NotificationModal";
import Pagination from "../components/Common/Pagination";
import ToastMessage from "../components/Common/ToastMessage";
import {
  createNotification,
  deleteNotification,
  getNotification,
  getNotifications,
  updateNotification,
} from "../lib/api";
import Label from "../components/Common/Label";

const PAGE_SIZE = 10;

const emptyFormState = {
  active: false,
  markdownContent: "",
};

const formatDateTime = (value) => {
  if (!value) return "--";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const buildPreviewText = (value = "", maxLength = 160) => {
  const normalized = value.replace(/\s+/g, " ").trim();
  if (!normalized) return "No content";
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength)}...`;
};

const areFormsEqual = (left, right) =>
  left.active === right.active &&
  left.markdownContent === right.markdownContent;

const PopupListView = ({
  currentPage,
  isLoading,
  onDelete,
  onEdit,
  onPageChange,
  onCreate,
  pageCount,
  popups,
  totalCount,
}) => (
  <div className="flex flex-col gap-6">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h2 className="text-[#000000DE] dark:text-white font-semibold text-xl leading-5 lg:text-2xl lg:leading-6 tracking-[0.036px]">
          Popup Configuration
        </h2>
        <p className="text-dark-grey dark:text-white/70 text-sm mt-2">
          Create and manage popup notifications shown to users.
        </p>
      </div>
      <Button
        className="flex items-center justify-center gap-2 bg-theme text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-theme/90 transition w-full sm:w-auto"
        onClick={onCreate}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M12 5V19" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
          <path d="M5 12H19" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        Create Popup
      </Button>
    </div>

    <div className="rounded-2xl border border-[#E6E8EC] bg-white dark:bg-darkThemeModalBg dark:border-white/10 shadow-appShadow overflow-hidden">
      <div className="border-b border-[#E6E8EC] dark:border-white/10 px-5 py-4 flex items-center justify-between">
        <h3 className="text-base font-semibold text-dark-grey dark:text-white">Popups</h3>
        <span className="text-sm text-dark-grey/70 dark:text-white/60">{totalCount} total</span>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center min-h-64">
          <LoadingSpinner />
        </div>
      ) : popups.length === 0 ? (
        <div className="px-5 py-16 text-center">
          <p className="text-lg font-semibold text-dark-grey dark:text-white">No popups found</p>
          <p className="mt-2 text-sm text-dark-grey/70 dark:text-white/60">
            Create the first popup to start managing notification content.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-[#E6E8EC] dark:divide-white/10">
          {popups.map((popup) => (
            <div
              key={popup.unid}
              className="px-5 py-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"
            >
              <Button
                className="text-left flex-1"
                onClick={() => onEdit(popup.unid)}
              >
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-base font-semibold text-dark-grey dark:text-white">
                    {buildPreviewText(popup.markdownContent, 72)}
                  </span>
                  <span
                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                      popup.active
                        ? "bg-[#DDF5ED] text-[#0B8F6A]"
                        : "bg-[#F1F2F4] text-[#5F6368] dark:bg-white/10 dark:text-white/70"
                    }`}
                  >
                    {popup.active ? "Active" : "Inactive"}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2 text-sm text-dark-grey/70 dark:text-white/60">
                  <span>Created {formatDateTime(popup.createdAt)}</span>
                  <span>{popup.numViewedBy} views</span>
                  <span>{popup.unid}</span>
                </div>
              </Button>

              <div className="flex items-center gap-3">
                <Button
                  className="px-4 py-2 rounded-md border border-theme text-theme text-sm font-semibold hover:bg-theme hover:text-white transition"
                  onClick={() => onEdit(popup.unid)}
                >
                  Edit
                </Button>
                <Button
                  className="px-4 py-2 rounded-md border border-[#EF4444] text-[#EF4444] text-sm font-semibold hover:bg-[#EF4444] hover:text-white transition"
                  onClick={() => onDelete(popup)}
                >
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>

    {pageCount > 1 && (
      <Pagination
        currentPage={currentPage}
        pageCount={pageCount}
        onPageChange={({ selected }) => onPageChange(selected)}
      />
    )}
  </div>
);

const PopupEditorView = ({
  formState,
  initialFormState,
  isCreating,
  isDeleting,
  isLoading,
  isSaving,
  onBack,
  onDelete,
  onPreview,
  onSave,
  popupMeta,
  setFormState,
}) => {
  const trimmedContent = formState.markdownContent.trim();
  const hasChanges = !areFormsEqual(formState, initialFormState);
  const isSaveDisabled = isLoading || isSaving || !trimmedContent || !hasChanges;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <Button
            className="inline-flex items-center gap-2 text-sm font-semibold text-theme hover:text-theme/80 transition"
            onClick={onBack}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Back to Popups
          </Button>
          <h2 className="mt-4 text-[#000000DE] dark:text-white font-semibold text-xl leading-5 lg:text-2xl lg:leading-6 tracking-[0.036px]">
            {isCreating ? "Create Popup" : "Edit Popup"}
          </h2>
          <p className="text-dark-grey dark:text-white/70 text-sm mt-2">
            {isCreating
              ? "Draft a new popup notification for users."
              : "Update popup content and publishing state."}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          <Button
            className="px-4 py-2 rounded-md border border-theme text-theme text-sm font-semibold hover:bg-theme hover:text-white transition w-full sm:w-auto"
            onClick={onPreview}
            disabled={!trimmedContent}
          >
            Preview
          </Button>
          {!isCreating && (
            <Button
              className="px-4 py-2 rounded-md border border-[#EF4444] text-[#EF4444] text-sm font-semibold hover:bg-[#EF4444] hover:text-white transition w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={onDelete}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          )}
          <Button
            className="px-4 py-2 rounded-md bg-theme text-white text-sm font-semibold hover:bg-theme/90 transition w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onSave}
            disabled={isSaveDisabled}
          >
            {isSaving ? "Saving..." : "Save Popup"}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center min-h-80">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          {!isCreating && popupMeta && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-2xl border border-[#E6E8EC] bg-white dark:bg-darkThemeModalBg dark:border-white/10 p-5 shadow-appShadow">
                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-dark-grey/60 dark:text-white/60">
                  Popup ID
                </p>
                <p className="mt-3 text-sm font-semibold text-dark-grey dark:text-white break-all">
                  {popupMeta.unid}
                </p>
              </div>
              <div className="rounded-2xl border border-[#E6E8EC] bg-white dark:bg-darkThemeModalBg dark:border-white/10 p-5 shadow-appShadow">
                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-dark-grey/60 dark:text-white/60">
                  Created
                </p>
                <p className="mt-3 text-sm font-semibold text-dark-grey dark:text-white">
                  {formatDateTime(popupMeta.createdAt)}
                </p>
              </div>
              <div className="rounded-2xl border border-[#E6E8EC] bg-white dark:bg-darkThemeModalBg dark:border-white/10 p-5 shadow-appShadow">
                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-dark-grey/60 dark:text-white/60">
                  Viewed By
                </p>
                <p className="mt-3 text-sm font-semibold text-dark-grey dark:text-white">
                  {popupMeta.numViewedBy}
                </p>
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-[#E6E8EC] bg-white dark:bg-darkThemeModalBg dark:border-white/10 shadow-appShadow p-5 sm:p-6">
            <div className="flex flex-col gap-6">
              <Checkbox
                checked={formState.active}
                label="Active"
                onChange={(checked) => {
                  setFormState((current) => ({
                    ...current,
                    active: checked,
                  }));
                }}
              />

              <div className="flex flex-col gap-2">
                <Label
                  htmlFor="popup-markdown-content"
                  className="text-sm font-semibold text-dark-grey dark:text-white"
                >
                  Markdown content
                </Label>
                <textarea
                  id="popup-markdown-content"
                  value={formState.markdownContent}
                  onChange={(event) => {
                    const { value } = event.target;
                    setFormState((current) => ({
                      ...current,
                      markdownContent: value,
                    }));
                  }}
                  placeholder="Write popup markdown here..."
                  className="min-h-[360px] w-full rounded-2xl border border-[#D4D7DD] dark:border-white/10 bg-white dark:bg-darkThemeBg px-4 py-3 text-sm text-dark-grey dark:text-white outline-none focus:border-theme resize-y"
                />
                <div className="flex items-center justify-between gap-3 text-xs text-dark-grey/60 dark:text-white/50">
                  <span>Markdown is rendered in the preview modal.</span>
                  <span>{trimmedContent.length} characters</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const PopupConfiguration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { popupId } = useParams();
  const isCreating = popupId === "new";
  const isEditing = Boolean(popupId) && popupId !== "new";
  const isListView = !popupId;

  const [popups, setPopups] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [isListLoading, setIsListLoading] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [previewContent, setPreviewContent] = useState("");
  const [formState, setFormState] = useState(emptyFormState);
  const [initialFormState, setInitialFormState] = useState(emptyFormState);
  const [popupMeta, setPopupMeta] = useState(null);
  const [popupToDelete, setPopupToDelete] = useState(null);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  const pageCount = Math.ceil(totalCount / PAGE_SIZE);

  const showToast = useCallback((message, type = "success") => {
    setToast({
      show: true,
      message,
      type,
    });
  }, []);

  const loadPopups = useCallback(async () => {
    setIsListLoading(true);
    try {
      const response = await getNotifications({
        pageNumber: currentPage + 1,
        pageSize: PAGE_SIZE,
      });
      const payload = response?.data?.data || {};
      setPopups(payload.notifications || []);
      setTotalCount(payload.count || 0);
    } catch (error) {
      setPopups([]);
      setTotalCount(0);
      showToast(error.message || "Failed to fetch popups.", "failed");
    } finally {
      setIsListLoading(false);
    }
  }, [currentPage, showToast]);

  const loadPopup = useCallback(async () => {
    if (!isEditing) return;

    setIsDetailLoading(true);
    try {
      const response = await getNotification(popupId);
      const popup = response?.data?.data?.notification;

      if (!popup) {
        throw new Error("Popup not found.");
      }

      const nextFormState = {
        active: Boolean(popup.active),
        markdownContent: popup.markdownContent || "",
      };

      setFormState(nextFormState);
      setInitialFormState(nextFormState);
      setPopupMeta(popup);
    } catch (error) {
      showToast(error.message || "Failed to fetch popup.", "failed");
      navigate("/popup-configuration", { replace: true });
    } finally {
      setIsDetailLoading(false);
    }
  }, [isEditing, navigate, popupId, showToast]);

  useEffect(() => {
    const toastState = location.state?.toast;
    if (!toastState) return;

    showToast(toastState.message, toastState.type);
    navigate(location.pathname, {
      replace: true,
      state: null,
    });
  }, [location.pathname, location.state, navigate, showToast]);

  useEffect(() => {
    if (isListView) {
      loadPopups();
    }
  }, [isListView, loadPopups]);

  useEffect(() => {
    if (isCreating) {
      setFormState(emptyFormState);
      setInitialFormState(emptyFormState);
      setPopupMeta(null);
      setIsDetailLoading(false);
      return;
    }

    if (isEditing) {
      loadPopup();
    }
  }, [isCreating, isEditing, loadPopup]);

  const handleSave = async () => {
    const payload = {
      active: formState.active,
      markdownContent: formState.markdownContent.trim(),
    };

    if (!payload.markdownContent) {
      showToast("Markdown content is required.", "failed");
      return;
    }

    setIsSaving(true);
    try {
      if (isCreating) {
        const response = await createNotification(payload);
        const createdPopup = response?.data?.data?.notification;

        if (!createdPopup?.unid) {
          throw new Error("Popup was created but no id was returned.");
        }

        navigate(`/popup-configuration/${createdPopup.unid}`, {
          replace: true,
          state: {
            toast: {
              message: "Popup created successfully.",
              type: "success",
            },
          },
        });
        return;
      }

      const response = await updateNotification(popupId, payload);
      const updatedPopup = response?.data?.data?.notification;
      const nextFormState = {
        active: Boolean(updatedPopup?.active ?? payload.active),
        markdownContent: updatedPopup?.markdownContent ?? payload.markdownContent,
      };

      setFormState(nextFormState);
      setInitialFormState(nextFormState);
      setPopupMeta(updatedPopup || popupMeta);
      showToast("Popup updated successfully.", "success");
    } catch (error) {
      showToast(error.message || "Failed to save popup.", "failed");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!popupToDelete?.unid) return;

    setIsDeleting(true);
    try {
      await deleteNotification(popupToDelete.unid);
      setPopupToDelete(null);

      if (isEditing && popupToDelete.unid === popupId) {
        navigate("/popup-configuration", {
          replace: true,
          state: {
            toast: {
              message: "Popup deleted successfully.",
              type: "success",
            },
          },
        });
        return;
      }

      showToast("Popup deleted successfully.", "success");

      const nextPageCount = Math.ceil(Math.max(totalCount - 1, 0) / PAGE_SIZE);
      if (currentPage > 0 && currentPage >= nextPageCount) {
        setCurrentPage(Math.max(nextPageCount - 1, 0));
      } else {
        loadPopups();
      }
    } catch (error) {
      showToast(error.message || "Failed to delete popup.", "failed");
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    if (!isListView) return;
    if (currentPage > 0 && pageCount > 0 && currentPage > pageCount - 1) {
      setCurrentPage(pageCount - 1);
    }
  }, [currentPage, isListView, pageCount]);

  const deleteConfirmMessage = useMemo(() => {
    if (!popupToDelete) return "";

    return `This popup will be permanently deleted.\n\n${buildPreviewText(
      popupToDelete.markdownContent,
      120
    )}`;
  }, [popupToDelete]);

  return (
    <>
      {isListView ? (
        <PopupListView
          currentPage={currentPage}
          isLoading={isListLoading}
          onCreate={() => navigate("/popup-configuration/new")}
          onDelete={(popup) => setPopupToDelete(popup)}
          onEdit={(unid) => navigate(`/popup-configuration/${unid}`)}
          onPageChange={setCurrentPage}
          pageCount={pageCount}
          popups={popups}
          totalCount={totalCount}
        />
      ) : (
        <PopupEditorView
          formState={formState}
          initialFormState={initialFormState}
          isCreating={isCreating}
          isDeleting={isDeleting}
          isLoading={isDetailLoading}
          isSaving={isSaving}
          onBack={() => navigate("/popup-configuration")}
          onDelete={() => {
            if (!popupMeta) return;
            setPopupToDelete(popupMeta);
          }}
          onPreview={() => setPreviewContent(formState.markdownContent.trim())}
          onSave={handleSave}
          popupMeta={popupMeta}
          setFormState={setFormState}
        />
      )}

      {previewContent && (
        <NotificationModal
          markdownContent={previewContent}
          onClose={() => setPreviewContent("")}
        />
      )}

      {popupToDelete && (
        <ConfirmModal
          title="Delete Popup"
          subText="This action cannot be undone."
          confirmMessage={deleteConfirmMessage}
          cancelText="Cancel"
          submitText={isDeleting ? "Deleting..." : "Delete Popup"}
          onCancel={() => {
            if (isDeleting) return;
            setPopupToDelete(null);
          }}
          onSubmit={handleDeleteConfirmed}
        />
      )}

      <ToastMessage
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={() =>
          setToast((current) => ({
            ...current,
            show: false,
          }))
        }
      />
    </>
  );
};

export default PopupConfiguration;
